import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import { FormattedMessage } from 'react-intl-next';
import { EmojiPicker } from '@atlaskit/emoji/picker';
import { Manager, Popper, Reference } from '@atlaskit/popper';
import { layers } from '@atlaskit/theme/constants';
import { useCloseManager } from '../../hooks/useCloseManager';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { messages } from '../../shared/i18n';
import { PickerRender } from '../../ufo';
import { Selector } from '../Selector';
import { Trigger } from '../Trigger';
import { contentStyle, pickerStyle, popupStyle, popupWrapperStyle } from './styles';
import { RepositionOnUpdate } from './RepositionOnUpdate';

/**
 * Test id for wrapper ReactionPicker div
 */
export var RENDER_REACTIONPICKER_TESTID = 'reactionPicker-testid';

/**
 * Test id for ReactionPicker panel div
 */
export var RENDER_REACTIONPICKERPANEL_TESTID = 'reactionPickerPanel-testid';

/**
 * Emoji Picker Controller Id for Accessibility Labels
 */
var PICKER_CONTROL_ID = 'emoji-picker';
var popperModifiers = [
/**
  Removing this applyStyle modifier as it throws client errors ref:
 https://popper.js.org/docs/v1/#modifiers
 https://popper.js.org/docs/v1/#modifiers..applyStyle
 { name: 'applyStyle', enabled: false },
  */
{
  name: 'hide',
  enabled: false
}, {
  name: 'offset',
  enabled: true,
  options: {
    offset: [0, 0]
  }
}, {
  name: 'flip',
  enabled: true,
  options: {
    flipVariations: true,
    boundariesElement: 'scrollParent'
  }
}, {
  name: 'preventOverflow',
  enabled: true
}];
/**
 * Picker component for adding reactions
 */
export var ReactionPicker = /*#__PURE__*/React.memo(function (props) {
  var miniMode = props.miniMode,
    className = props.className,
    emojiProvider = props.emojiProvider,
    onSelection = props.onSelection,
    allowAllEmojis = props.allowAllEmojis,
    disabled = props.disabled,
    pickerQuickReactionEmojiIds = props.pickerQuickReactionEmojiIds,
    _props$onShowMore = props.onShowMore,
    onShowMore = _props$onShowMore === void 0 ? function () {} : _props$onShowMore,
    _props$onOpen = props.onOpen,
    onOpen = _props$onOpen === void 0 ? function () {} : _props$onOpen,
    _props$onCancel = props.onCancel,
    onCancel = _props$onCancel === void 0 ? function () {} : _props$onCancel,
    _props$tooltipContent = props.tooltipContent,
    tooltipContent = _props$tooltipContent === void 0 ? jsx(FormattedMessage, messages.addReaction) : _props$tooltipContent,
    emojiPickerSize = props.emojiPickerSize,
    _props$showOpaqueBack = props.showOpaqueBackground,
    showOpaqueBackground = _props$showOpaqueBack === void 0 ? false : _props$showOpaqueBack,
    _props$subtleReaction = props.subtleReactionsSummaryAndPicker,
    subtleReactionsSummaryAndPicker = _props$subtleReaction === void 0 ? false : _props$subtleReaction,
    _props$showAddReactio = props.showAddReactionText,
    showAddReactionText = _props$showAddReactio === void 0 ? false : _props$showAddReactio;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    triggerRef = _useState2[0],
    setTriggerRef = _useState2[1];

  /**
   * Container <div /> reference (used by custom hook to detect click outside)
   */
  var wrapperRef = useRef(null);
  var updatePopper = useRef();
  var _useState3 = useState({
      /**
       * Show the picker floating panel
       */
      isOpen: false,
      /**
       * Show the full custom emoji list picker or the default list of emojis
       */
      showFullPicker: !!allowAllEmojis && Array.isArray(pickerQuickReactionEmojiIds) && pickerQuickReactionEmojiIds.length === 0
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    settings = _useState4[0],
    setSettings = _useState4[1];

  /**
   * Custom hook triggers when user clicks outside the reactions picker
   */
  useCloseManager(wrapperRef, function (callbackType) {
    close();
    onCancel();
    if (triggerRef && callbackType === 'ESCAPE') {
      requestAnimationFrame(function () {
        return triggerRef.focus();
      });
    }
  }, true, settings.isOpen);

  /**
   * Event callback when the picker is closed
   * @param _id Optional id if an emoji button was selected or undefineed if was clicked outside the picker
   */
  var close = useCallback(function (_id) {
    setSettings(_objectSpread(_objectSpread({}, settings), {}, {
      isOpen: false
    }));
    // ufo abort reaction experience
    PickerRender.abort({
      metadata: {
        emojiId: _id,
        source: 'ReactionPicker',
        reason: 'close dialog'
      }
    });
  }, [settings]);

  /**
   * Event handle rwhen selecting to show the custom emoji icons picker
   * @param e event param
   */
  var onSelectMoreClick = useCallback(function (e) {
    e.preventDefault();
    setSettings({
      isOpen: true,
      showFullPicker: true
    });
    onShowMore();
  }, [onShowMore]);

  /**
   * Event callback when an emoji icon is selected
   * @param item selected item
   */
  var onEmojiSelected = useCallback(function (item) {
    // no emoji was selected
    if (!item.id) {
      return;
    }
    onSelection(item.id, settings.showFullPicker ? 'emojiPicker' : 'quickSelector');
    close(item.id);
  }, [close, onSelection, settings.showFullPicker]);

  /**
   * Event handler when the emoji icon to open the custom picker is selected
   */
  var onTriggerClick = function onTriggerClick() {
    // ufo start reactions picker open experience
    PickerRender.start();
    setSettings({
      isOpen: !settings.isOpen,
      showFullPicker: !!allowAllEmojis && Array.isArray(pickerQuickReactionEmojiIds) && pickerQuickReactionEmojiIds.length === 0
    });
    onOpen();
    // ufo reactions picker opened success
    PickerRender.success();
  };
  var wrapperClassName = " ".concat(settings.isOpen ? 'isOpen' : '', " ").concat(miniMode ? 'miniMode' : '', " ").concat(className);
  useLayoutEffect(function () {
    var _updatePopper$current;
    (_updatePopper$current = updatePopper.current) === null || _updatePopper$current === void 0 || _updatePopper$current.call(updatePopper);
  }, [settings]);
  return jsx("div", {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    className: wrapperClassName
    // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    ,
    css: pickerStyle,
    "data-testid": RENDER_REACTIONPICKER_TESTID,
    ref: wrapperRef
  }, jsx(Manager, null, jsx(Reference, null, function (_ref) {
    var _ref2 = _ref.ref;
    return (
      // Render a button to open the <Selector /> panel
      jsx(Trigger, {
        ariaAttributes: {
          'aria-expanded': settings.isOpen,
          'aria-controls': PICKER_CONTROL_ID
        },
        ref: function ref(node) {
          if (node && settings.isOpen) {
            if (typeof _ref2 === 'function') {
              _ref2(node);
            } else {
              _ref2.current = node;
            }
            setTriggerRef(node);
          }
        },
        onClick: onTriggerClick,
        miniMode: miniMode,
        disabled: disabled,
        tooltipContent: settings.isOpen ? null : tooltipContent,
        showOpaqueBackground: showOpaqueBackground,
        showAddReactionText: showAddReactionText,
        subtleReactionsSummaryAndPicker: subtleReactionsSummaryAndPicker
      })
    );
  }), settings.isOpen && jsx(PopperWrapper, {
    settings: settings
  }, settings.showFullPicker ? jsx(EmojiPicker, {
    emojiProvider: emojiProvider,
    onSelection: onEmojiSelected,
    size: emojiPickerSize
  }) :
  // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
  jsx("div", {
    css: contentStyle
  }, jsx(Selector, {
    emojiProvider: emojiProvider,
    onSelection: onEmojiSelected,
    showMore: allowAllEmojis,
    onMoreClick: onSelectMoreClick,
    pickerQuickReactionEmojiIds: pickerQuickReactionEmojiIds
  })))));
});
var PopperWrapper = function PopperWrapper(props) {
  var settings = props.settings,
    children = props.children;
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    popupRef = _useState6[0],
    setPopupRef = _useState6[1];
  /**
   * add focus lock to popup
   */
  useFocusTrap({
    initialFocusRef: null,
    targetRef: popupRef
  });
  return jsx(Popper, {
    placement: "bottom-start",
    modifiers: popperModifiers
  }, function (_ref3) {
    var _ref4 = _ref3.ref,
      style = _ref3.style,
      update = _ref3.update;
    return jsx("div", {
      id: PICKER_CONTROL_ID,
      "data-testid": RENDER_REACTIONPICKERPANEL_TESTID
      // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
      ,
      style: _objectSpread({
        zIndex: layers.layer()
      }, style),
      ref: function ref(node) {
        if (node) {
          if (typeof _ref4 === 'function') {
            _ref4(node);
          } else {
            _ref4.current = node;
          }
          setPopupRef(node);
        }
      }
      // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
      ,
      css: popupWrapperStyle,
      tabIndex: 0
    }, jsx(RepositionOnUpdate, {
      update: update,
      settings: settings
    }, jsx("div", {
      css: popupStyle
    }, children)));
  });
};